import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import {
  E_KHATTABAT_102_CONVERSATION_STATUS,
  E_KHATTABAT_CONVERSATION_TYPE,
  TGender,
  TKhattabatMessage,
  TMediaType,
  TMessagesType,
  TOneOTwoData
} from "../../../Types";
import {
  createDateFromSpecificDateString,
  getDefaultDate
} from "../../../helpers/et3-date-helper";
import {
  isDefinedArray,
  isDefinedString,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import getCoreUIStore from "../../core/core-ui-store";
import { apiCallUpdateOneOTwoQuality } from "../../../api/khattbat-api/api-call-update-one-o-two-quality";
import getKhattabatStore from "..";
import getCorePopUpStore from "../../core/core-popup-store";
import {
  KHATTABAT_ONE_O_TWO_DEFAULT_MESSAGE_METADATA,
  KHATTABAT_ONE_O_TWO_MIN_QUALITY_MESSAGES
} from "../../../constants/core-constants";
import { DEFAULT_TIME } from "../../../constants/input-entries-constants";
import { apiCallSendOneOTwoMedia } from "../../../api/khattbat-api/api-call-send-one-o-two-media";
import { Ti18nKeys } from "../../../constants/i18n";
import getKhattabat102GeneralStore from "./general-102-store";
import {
  E_NOTIFICATION_REACTION_ACTION,
  E_NOTIFICATION_TYPE
} from "../../notification-store/constants";
import { TNotificationPayloadMap } from "../../notification-store/types";
import getKhattabatSharedMessagingStore from "../messaging-stores/conversation-messaging-store";
import getCorePersonalStore from "../../core/core-personal-store";
import getCoreTimingStore from "../../core/core-timing-store";
import getCoreHistoryStore from "../../core/core-history-store";
import getKhattabat102MessagingStore from "../messaging-stores/102-messaging-store";
import getKhattabat101CStore from "../101c-stores";
import getKhattabatMediaStore from "../messaging-stores/conversation-media-store";
import {
  TKhattabatOneOTwoMessageDisplayedHistoryEvent,
  TKhattabatOneOTwoMessageDisplayedReaction,
  TKhattabatOneOTwoMessageHiddenAction,
  TKhattabatOneOTwoMessageHistoryEvent,
  TKhattabatOneOTwoMessageMetadata,
  TKhattabatOneOTwoMessageReaction,
  TKhattabatOneOTwoMessageReactionHistory
} from "../../../types/khattabat-one-o-two-message.type";
import { E_KHATTABAT_ONE_O_TWO_MESSAGE_HIDE_TYPE } from "../../../constants/enums/khattabat-one-o-two-message-hide-type.enum";
import { E_KHATTABAT_ONE_O_TWO_MESSAGE_HISTORY_EVENT_TYPE } from "../../../constants/enums/khattabat-one-o-two-message-history-event-type.enum";
import { deriveKhattabatOneOTwoHistoryEvents } from "../../../helpers/et3-khattabat-one-o-two-history-helper";

class Khattabat102Store {
  conversationID = observable.box<string>("");
  exitByMatch = observable.box<1 | 0>(0);
  exitByUser = observable.box<1 | 0>(0);
  exitedByMatchDate = observable.box<Date>(getDefaultDate());
  exitedByClientDate = observable.box<Date>(getDefaultDate());
  manualIsQuality = observable.box<number>(0);
  isMatchInAnotherOneOTwo = observable.box<boolean>(false);
  automaticIsQuality = observable.box<number>(0);
  // 102-specific reaction/hidden metadata, keyed by messageId. Kept separate
  // from the shared messaging store's message array so base message
  // rendering/sending stays on a single, un-duplicated source of truth.
  oneOTwoMessagesMetadata = observable.box<
    Record<number, TKhattabatOneOTwoMessageMetadata>
  >({});

  getOneOTwoMessagesMetadata = (): Record<
    number,
    TKhattabatOneOTwoMessageMetadata
  > => {
    return this.oneOTwoMessagesMetadata.get();
  };

  setOneOTwoMessagesMetadata = (
    oneOTwoMessagesMetadata: Record<number, TKhattabatOneOTwoMessageMetadata>
  ): void => {
    runInAction(() => {
      this.oneOTwoMessagesMetadata.set(oneOTwoMessagesMetadata);
    });
  };

  getMessageMetadata = (
    messageId: number
  ): TKhattabatOneOTwoMessageMetadata => {
    return reduce(
      () => this.getOneOTwoMessagesMetadata()[messageId],
      KHATTABAT_ONE_O_TWO_DEFAULT_MESSAGE_METADATA
    );
  };

  // reactionId is the active-reaction COUNT (0, 1, or 2), not a single
  // reaction's id - a message can have more than one active reaction, so
  // this must return all of them rather than the first match.
  getActiveMessageReactions = (
    messageId: number
  ): TKhattabatOneOTwoMessageReaction[] => {
    const { reactionId, reactions } = this.getMessageMetadata(messageId);
    if (!isPositiveNumber(reactionId)) {
      return [];
    }
    return reactions.filter((reaction) => isPositiveNumber(reaction.isActive));
  };

  // A reaction's own sender can differ from the reacted-to message's
  // sender (either party can react to either party's message), so the
  // reaction badge's gender must be resolved from the reaction's
  // senderUUID, not the message's senderGender. Only client messages carry a
  // positive flag of their own, so the gender is read off a client message and
  // flipped for the match since client/match are always opposite genders.
  getReactionSenderGender = (senderUUID: string): TGender => {
    const { clientReceiverUUID, matchReceiverUUID } =
      getKhattabat102MessagingStore(this.conversationID.get());
    const isClientReactor = senderUUID === clientReceiverUUID.get();
    const isMatchReactor = senderUUID === matchReceiverUUID.get();

    if (!isClientReactor && !isMatchReactor) {
      return "m";
    }

    const messages = getKhattabatSharedMessagingStore(
      this.conversationID.get()
    ).messages.get();
    const clientMessage = messages.find((message) =>
      isPositiveNumber(message.isClientMessage)
    );
    const clientGender = reduce(() => clientMessage.senderGender, "m");

    if (isClientReactor) {
      return clientGender;
    }
    return clientGender === "m" ? "f" : "m";
  };

  getIsMessageHidden = (messageId: number): boolean => {
    return isPositiveNumber(this.getMessageMetadata(messageId).isHidden);
  };

  getActiveMessageHiddenActions = (
    messageId: number
  ): TKhattabatOneOTwoMessageHiddenAction[] => {
    return this.getMessageMetadata(messageId).hiddenActions;
  };

  // A message can carry more than one simultaneously-active hidden action
  // (e.g. one party hid it "for_me" while another hid it "for_everyone"),
  // but the admin UI only ever shows a single indicator. "for_everyone" is
  // the more encompassing scope, so it takes precedence when both exist.
  getCurrentMessageHiddenAction = (
    messageId: number
  ): TKhattabatOneOTwoMessageHiddenAction | undefined => {
    const activeHiddenActions = this.getActiveMessageHiddenActions(messageId);
    return (
      activeHiddenActions.find(
        (hiddenAction) =>
          hiddenAction.hiddenType ===
          E_KHATTABAT_ONE_O_TWO_MESSAGE_HIDE_TYPE.FOR_EVERYONE
      ) ?? activeHiddenActions[0]
    );
  };

  getMessageReactionHistoryEvents = (
    messageId: number
  ): TKhattabatOneOTwoMessageHistoryEvent[] => {
    return deriveKhattabatOneOTwoHistoryEvents({
      historyRows: this.getMessageMetadata(messageId).reactionHistory,

      getEventLabel: (historyRow) => historyRow.reaction,

      getHistoryGroupKey: (historyRow) => historyRow.senderUUID,

      getRecipientUUID: (historyRow) => historyRow.recipientUUID
    });
  };

  getMessageHideHistoryEvents = (
    messageId: number
  ): TKhattabatOneOTwoMessageHistoryEvent[] => {
    return deriveKhattabatOneOTwoHistoryEvents({
      historyRows: this.getMessageMetadata(messageId).hideHistory,

      getEventLabel: (historyRow) => historyRow.hiddenType,

      getHistoryGroupKey: (historyRow) => historyRow.hiddenByUUID
    });
  };

  // Histories popup view-model: reaction- and hide-history events merged
  // into a single, newest-first list, each tagged with which list it came
  // from so the popup can pick the right icon/label.
  getMessageDisplayedHistoryEvents = (
    messageId: number
  ): TKhattabatOneOTwoMessageDisplayedHistoryEvent[] => {
    const reactionHistoryEvents: TKhattabatOneOTwoMessageDisplayedHistoryEvent[] =
      this.getMessageReactionHistoryEvents(messageId).map((event) => ({
        ...event,
        isHideEvent: false
      }));
    const hideHistoryEvents: TKhattabatOneOTwoMessageDisplayedHistoryEvent[] =
      this.getMessageHideHistoryEvents(messageId).map((event) => ({
        ...event,
        isHideEvent: true
      }));

    return [...reactionHistoryEvents, ...hideHistoryEvents].sort(
      (firstEvent, secondEvent) =>
        new Date(secondEvent.occurredOn).getTime() -
        new Date(firstEvent.occurredOn).getTime()
    );
  };

  // Reaction-bar view-model: up to the 2 active reactions, merged into a
  // single count-2 badge when they're identical.
  getDisplayedMessageReactions = (
    messageId: number
  ): {
    displayedReactions: TKhattabatOneOTwoMessageDisplayedReaction[];
    isMerged: boolean;
  } => {
    const activeReactions = this.getActiveMessageReactions(messageId).slice(
      0,
      2
    );

    const areSameReactions =
      activeReactions.length === 2 &&
      activeReactions[0].reaction === activeReactions[1].reaction;

    if (areSameReactions) {
      return {
        isMerged: true,
        displayedReactions: [
          {
            reaction: activeReactions[0],
            count: 2,
            reactorGender: this.getReactionSenderGender(
              activeReactions[0].senderUUID
            )
          }
        ]
      };
    }

    return {
      isMerged: false,
      displayedReactions: activeReactions.map((activeReaction) => ({
        reaction: activeReaction,
        count: 1,
        reactorGender: this.getReactionSenderGender(activeReaction.senderUUID)
      }))
    };
  };

  getHasMessageReactionHistory = (messageId: number): boolean => {
    return this.getMessageMetadata(messageId).reactionHistory.length > 0;
  };

  getShouldRenderMessageReactionBar = (messageId: number): boolean => {
    return (
      this.getActiveMessageReactions(messageId).length > 0 ||
      this.getHasMessageReactionHistory(messageId)
    );
  };

  getHasActiveMessageHiddenAction = (messageId: number): boolean => {
    return this.getMessageMetadata(messageId).hiddenActions.length > 0;
  };

  getIsMessageHiddenForEveryone = (messageId: number): boolean => {
    return (
      this.getCurrentMessageHiddenAction(messageId)?.hiddenType ===
      E_KHATTABAT_ONE_O_TWO_MESSAGE_HIDE_TYPE.FOR_EVERYONE
    );
  };

  // TKhattabatMessage's messageId is only ever present on 102 messages
  // (added by TKhattabatOneOTwoMessage), so this reads it defensively
  // rather than widening the shared message type.
  getOneOTwoMessageId = (message: TKhattabatMessage): number => {
    const candidateMessageId = (message as { messageId?: unknown }).messageId;
    return isPositiveNumber(candidateMessageId) ? candidateMessageId : 0;
  };

  // Reaction contribution to the C/M counters, derived from the same
  // ADDED/CHANGED/REMOVED history events the Histories popup already uses -
  // reused rather than recomputed so an Updated (emoji swap) or Removed
  // reaction never affects the count, only a genuine ADDED event does. This
  // is computed fresh from the stored reaction history on every call (never
  // an incremented local counter), so reloading the conversation can't
  // double-count it. The counter belongs to whoever performed the reaction
  // (actorUUID), not to the sender of the message being reacted to.
  getReactionCountersContribution = (): {
    clientReactionAddedCounter: number;
    matchReactionAddedCounter: number;
  } => {
    const { clientReceiverUUID, matchReceiverUUID } =
      getKhattabat102MessagingStore(this.conversationID.get());
    const clientUUID = clientReceiverUUID.get();
    const matchUUID = matchReceiverUUID.get();

    let clientReactionAddedCounter = 0;
    let matchReactionAddedCounter = 0;

    Object.keys(this.getOneOTwoMessagesMetadata()).forEach((messageIdKey) => {
      const messageId = Number(messageIdKey);
      this.getMessageReactionHistoryEvents(messageId).forEach((event) => {
        if (
          event.eventType !==
          E_KHATTABAT_ONE_O_TWO_MESSAGE_HISTORY_EVENT_TYPE.ADDED
        ) {
          return;
        }
        if (event.actorUUID === clientUUID) {
          clientReactionAddedCounter++;
        } else if (event.actorUUID === matchUUID) {
          matchReactionAddedCounter++;
        }
      });
    });

    return { clientReactionAddedCounter, matchReactionAddedCounter };
  };

  //MH: Column identity comes from the message's author - the sender column is
  // whoever sent the message, the recipient column is the other participant.
  getMessageHistoryColumns = (
    messageId: number
  ): {
    senderID: number;
    recipientID: number;
    senderGender: TGender;
    recipientGender: TGender;
    isKhattabaAuthored: boolean;
    senderEvents: TKhattabatOneOTwoMessageDisplayedHistoryEvent[];
    recipientEvents: TKhattabatOneOTwoMessageDisplayedHistoryEvent[];
  } => {
    const {
      clientReceiverID,
      matchReceiverID,
      clientReceiverUUID,
      matchReceiverUUID
    } = getKhattabat102MessagingStore(this.conversationID.get());
    const message = getKhattabatSharedMessagingStore(this.conversationID.get())
      .messages.get()
      .find(
        (candidateMessage) =>
          this.getOneOTwoMessageId(candidateMessage) === messageId
      );

    const isClientAuthored = isPositiveNumber(
      reduce(() => message.isClientMessage, 0)
    );
    const isKhattabaAuthored = isPositiveNumber(
      reduce(() => message.isKhattabaMessage, 0)
    );
    const senderUUID = isClientAuthored
      ? clientReceiverUUID.get()
      : matchReceiverUUID.get();
    const recipientUUID = isClientAuthored
      ? matchReceiverUUID.get()
      : clientReceiverUUID.get();

    const messageSenderGender = reduce(() => message.senderGender, "m");
    const senderGender = isKhattabaAuthored
      ? this.getReactionSenderGender(senderUUID)
      : messageSenderGender;
    const recipientGender = isKhattabaAuthored
      ? this.getReactionSenderGender(recipientUUID)
      : messageSenderGender === "m"
        ? "f"
        : "m";

    const historyEvents = this.getMessageDisplayedHistoryEvents(messageId);

    return {
      senderID: isClientAuthored
        ? clientReceiverID.get()
        : matchReceiverID.get(),
      recipientID: isClientAuthored
        ? matchReceiverID.get()
        : clientReceiverID.get(),
      senderGender,
      recipientGender,
      isKhattabaAuthored,
      senderEvents: historyEvents.filter(
        (event) => event.actorUUID === senderUUID
      ),
      recipientEvents: historyEvents.filter(
        (event) => event.actorUUID === recipientUUID
      )
    };
  };
  getMessagesCounters = () => {
    const messages = getKhattabatSharedMessagingStore(
      this.conversationID.get()
    ).messages.get();
    let khattabaMessagesCounter = 0;
    let clientMessagesCounter = 0;
    let matchMessagesCounter = 0;
    if (isDefinedArray(messages)) {
      messages.forEach((message) => {
        if (isPositiveNumber(message.isKhattabaMessage)) {
          khattabaMessagesCounter++;
        } else if (isPositiveNumber(message.isClientMessage)) {
          clientMessagesCounter++;
        } else {
          matchMessagesCounter++;
        }
      });
    }

    const { clientReactionAddedCounter, matchReactionAddedCounter } =
      this.getReactionCountersContribution();

    return {
      khattabaMessagesCounter,
      clientMessagesCounter: clientMessagesCounter + clientReactionAddedCounter,
      matchMessagesCounter: matchMessagesCounter + matchReactionAddedCounter
    };
  };

  setOneOTwoExitState = ({
    exitByMatch,
    exitByUser,
    exitedByMatchDate,
    exitedByClientDate
  }: {
    exitByMatch: 1 | 0;
    exitByUser: 1 | 0;
    exitedByMatchDate: Date;
    exitedByClientDate: Date;
  }) => {
    runInAction(() => {
      this.exitByMatch.set(reduce(() => exitByMatch, this.exitByMatch.get()));
      this.exitByUser.set(reduce(() => exitByUser, this.exitByUser.get()));
      this.exitedByMatchDate.set(
        reduce(() => exitedByMatchDate, this.exitedByMatchDate.get())
      );
      this.exitedByClientDate.set(
        reduce(() => exitedByClientDate, this.exitedByClientDate.get())
      );
    });
  };

  setOneOTwoConversationData = ({
    data,
    userID
  }: {
    data: TOneOTwoData[string];
    userID: number;
  }) => {
    const { conversationType, updateMessages } =
      getKhattabatSharedMessagingStore(this.conversationID.get());
    const {
      senderID,
      senderUUID,
      senderName,
      matchReceiverID,
      matchReceiverUUID,
      matchReceiverName,
      clientReceiverID,
      clientReceiverUUID,
      clientReceiverName,
      isSilentBanned
    } = getKhattabat102MessagingStore(this.conversationID.get());
    const { userUUID, userName } = getKhattabat101CStore(userID);
    const messages = reduce(() => data.messages, []);
    const oneOTwoMessagesMetadata = messages.reduce<
      Record<number, TKhattabatOneOTwoMessageMetadata>
    >((accumulatedMetadata, message) => {
      accumulatedMetadata[message.messageId] = {
        reactionId: message.reactionId,
        reactions: message.reactions,
        reactionHistory: message.reactionHistory,
        isHidden: message.isHidden,
        hiddenActions: message.hiddenActions,
        hideHistory: message.hideHistory
      };
      return accumulatedMetadata;
    }, {});

    runInAction(() => {
      this.exitByMatch.set(reduce(() => data.exitByMatch, 0));
      this.exitByUser.set(reduce(() => data.exitByUser, 0));
      this.exitedByMatchDate.set(
        reduce(() => data.exitedByMatchDate, getDefaultDate())
      );
      this.exitedByClientDate.set(
        reduce(() => data.exitedByClientDate, getDefaultDate())
      );
      this.manualIsQuality.set(reduce(() => data.manualIsQuality, 0));
      this.automaticIsQuality.set(reduce(() => data.automaticIsQuality, 0));
      this.isMatchInAnotherOneOTwo.set(
        reduce(() => data.isMatchInAnotherOneOTwo, false)
      );

      conversationType.set("102");
      matchReceiverID.set(reduce(() => data.peerID, 0));
      matchReceiverUUID.set(reduce(() => data.peerUUID, ""));
      matchReceiverName.set(reduce(() => data.matchUserName, ""));
      isSilentBanned.set(reduce(() => data.isSilentBanned, 0));
      clientReceiverID.set(userID);
      clientReceiverUUID.set(userUUID.get());
      clientReceiverName.set(userName.get());
      updateMessages(messages);
      this.setOneOTwoMessagesMetadata(oneOTwoMessagesMetadata);

      senderID.set(getCorePersonalStore().user.get().id);
      senderUUID.set(getKhattabatStore().alkhattabaUUID.get());
      senderName.set(getCorePersonalStore().user.get().fullName);
    });
  };
  toggleOneTwoQualityState = async ({ isAuto }: { isAuto: number }) => {
    const oldState = isPositiveNumber(this.manualIsQuality.get());
    const newState = oldState ? 0 : 1;
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", true);
    const { status, code } = await apiCallUpdateOneOTwoQuality({
      oneOTwoConversationID: this.conversationID.get(),
      newState,
      isAuto
    });
    getCoreUIStore().setIsLoading("KHATTABAT_CLIENTS", false);
    if (status === "OK") {
      const { clientReceiverID } = getKhattabat102MessagingStore(
        this.conversationID.get()
      );
      runInAction(() => {
        this.manualIsQuality.set(newState);
        if (isPositiveNumber(isAuto)) {
          this.automaticIsQuality.set(1);
        }
        getKhattabat102GeneralStore().handleClientIsAlmostExpiredAndNo102QualityUpdate(
          {
            clientID: clientReceiverID.get()
          }
        );
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  shouldSetOneOTwoToQuality = async () => {
    const alreadyUpdatedByAuto = isPositiveNumber(
      this.automaticIsQuality.get()
    );
    if (alreadyUpdatedByAuto) {
      return false;
    }
    const { clientMessagesCounter, matchMessagesCounter } =
      this.getMessagesCounters();

    const isQualityMatchMessagesCounter =
      matchMessagesCounter >= KHATTABAT_ONE_O_TWO_MIN_QUALITY_MESSAGES;

    const isQualityClientMessagesCounter =
      clientMessagesCounter >= KHATTABAT_ONE_O_TWO_MIN_QUALITY_MESSAGES;

    const manualIsQuality =
      isQualityMatchMessagesCounter && isQualityClientMessagesCounter;

    if (manualIsQuality) {
      await this.toggleOneTwoQualityState({ isAuto: 1 });
    }
    return true;
  };
  appendReceivedMessageFromNotification = ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_MESSAGE];
  }): boolean => {
    const sharedStore = getKhattabatSharedMessagingStore(
      this.conversationID.get()
    );
    const isConversationLoaded =
      sharedStore.conversationType.get() ===
      E_KHATTABAT_CONVERSATION_TYPE.ONE_O_TWO;
    if (!isConversationLoaded) {
      return false;
    }

    sharedStore.appendReceivedMessage({
      message: reduce(() => payload.message, ""),
      messageType: reduce(() => payload.messageType, "text"),
      senderGender: reduce(() => payload.gender, ""),
      senderUUID: reduce(() => payload.senderUUID, "")
    });
    return true;
  };
  handleReactionNotification = async ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_MESSAGE_REACTION];
  }) => {
    const wasReactionApplied = this.applyReactionFromNotification({ payload });
    if (!wasReactionApplied) {
      await getCoreTimingStore().refreshKhattabatPageData(
        getCoreHistoryStore().getPageName()
      );
    }
  };
  applyReactionFromNotification = ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_MESSAGE_REACTION];
  }): boolean => {
    const sharedStore = getKhattabatSharedMessagingStore(
      this.conversationID.get()
    );
    const messageID = reduce(() => payload.messageID, 0);
    const isMessageLoaded =
      sharedStore.conversationType.get() ===
        E_KHATTABAT_CONVERSATION_TYPE.ONE_O_TWO &&
      sharedStore.messages
        .get()
        .some(
          (loadedMessage) =>
            this.getOneOTwoMessageId(loadedMessage) === messageID
        );
    if (!isMessageLoaded) {
      return false;
    }

    const senderUUID = reduce(() => payload.senderUUID, "");
    const recipientUUID = reduce(() => payload.recipientUUID, "");
    const reaction = reduce(() => payload.reaction, "");
    const reactedOn = reduce(() => payload.reactedOn, DEFAULT_TIME);
    const isRemoval =
      reduce(() => payload.action, E_NOTIFICATION_REACTION_ACTION.REMOVE) ===
      E_NOTIFICATION_REACTION_ACTION.REMOVE;
    const metadata = this.getMessageMetadata(messageID);

    const hasActorReaction = metadata.reactions.some(
      (existingReaction) => existingReaction.senderUUID === senderUUID
    );
    const reactionToApply: TKhattabatOneOTwoMessageReaction = {
      senderUUID,
      recipientUUID,
      reaction,
      isActive: 1,
      isSeen: 0,
      sentOn: reactedOn,
      updatedOn: reactedOn,
      deletedOn: DEFAULT_TIME,
      seenOn: DEFAULT_TIME
    };
    const reactions: TKhattabatOneOTwoMessageReaction[] = (() => {
      if (isRemoval) {
        return metadata.reactions.filter(
          (existingReaction) => existingReaction.senderUUID !== senderUUID
        );
      }
      if (hasActorReaction) {
        return metadata.reactions.map((existingReaction) =>
          existingReaction.senderUUID === senderUUID
            ? { ...existingReaction, reaction, updatedOn: reactedOn }
            : existingReaction
        );
      }
      return [...metadata.reactions, reactionToApply];
    })();

    const closedReactionHistory = metadata.reactionHistory.map(
      (historyRow): TKhattabatOneOTwoMessageReactionHistory =>
        historyRow.senderUUID === senderUUID &&
        isPositiveNumber(historyRow.isActive)
          ? { ...historyRow, isActive: 0, deactivatedOn: reactedOn }
          : historyRow
    );
    const reactionHistory: TKhattabatOneOTwoMessageReactionHistory[] = isRemoval
      ? closedReactionHistory
      : [
          ...closedReactionHistory,
          {
            senderUUID,
            recipientUUID,
            reaction,
            isActive: 1,
            createdOn: reactedOn,
            deactivatedOn: DEFAULT_TIME
          }
        ];

    this.setOneOTwoMessagesMetadata({
      ...this.getOneOTwoMessagesMetadata(),
      [messageID]: {
        ...metadata,
        reactionId: reactions.length,
        reactions,
        reactionHistory
      }
    });
    return true;
  };

  applyExitFromNotification = ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_EXITED];
  }): boolean => {
    const isConversationLoaded =
      getKhattabatSharedMessagingStore(
        this.conversationID.get()
      ).conversationType.get() === E_KHATTABAT_CONVERSATION_TYPE.ONE_O_TWO;
    const exitedByUUID = reduce(() => payload.exitedByUUID, "");
    if (!isConversationLoaded || !isDefinedString(exitedByUUID)) {
      return false;
    }

    const exitedOn = createDateFromSpecificDateString(
      reduce(() => payload.exitedOn, DEFAULT_TIME)
    );
    const { clientReceiverUUID, matchReceiverUUID } =
      getKhattabat102MessagingStore(this.conversationID.get());

    switch (exitedByUUID) {
      case clientReceiverUUID.get():
        runInAction(() => {
          this.exitByUser.set(1);
          this.exitedByClientDate.set(exitedOn);
        });
        return true;
      case matchReceiverUUID.get():
        runInAction(() => {
          this.exitByMatch.set(1);
          this.exitedByMatchDate.set(exitedOn);
        });
        return true;
      default:
        return false;
    }
  };

  handleSendOneOTwoMessageClient = ({
    message,
    databaseCurrentTime,
    type
  }: {
    message: string;
    databaseCurrentTime: string;
    type: TMessagesType;
  }) => {
    const oneOTwoMessageToAdd: TKhattabatMessage = {
      isKhattabaMessage: 1,
      isClientMessage: 0,
      message,
      messageType: type,
      createdOn: databaseCurrentTime,
      isSeen: 0,
      senderGender: "f",
      conversationId: this.conversationID.get()
    };
    getKhattabatSharedMessagingStore(this.conversationID.get()).appendMessage(
      oneOTwoMessageToAdd
    );
  };

  sendOneOTwoMediaDataBase = async ({ type }: { type: TMediaType }) => {
    const { status, code, data } = await apiCallSendOneOTwoMedia({
      oneOTwoConversationId: this.conversationID.get(),
      type,
      attachment: getKhattabatMediaStore(
        this.conversationID.get()
      ).imageToBeUploadedBase64.get(),
      duration: 0
    });
    if (status === "OK") {
      const databaseCurrentTime = reduce(
        () => data.messageCurrentTime,
        DEFAULT_TIME
      );
      const mediaMessage = reduce(() => data.mideaMessage, "");
      this.handleSendOneOTwoMessageClient({
        message: mediaMessage,
        databaseCurrentTime,
        type
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };
  getConversationHeaderStatusValues = (): {
    conversationStatuskey: Ti18nKeys;
    conversationStatusImageName: string;
    conversationExitDate: Date;
  } => {
    const pendingInteraction = !isDefinedArray(
      getKhattabatSharedMessagingStore(this.conversationID.get()).messages.get()
    );
    const exitedByMatch = this.exitByMatch.get();
    const exitedByClient = this.exitByUser.get();
    const exitedByClientDate = this.exitedByClientDate.get();
    const exitedByMatchDate = this.exitedByMatchDate.get();

    let conversationStatuskey: Ti18nKeys = "ONGOING";
    let conversationStatusImageName = "green-ellipse";
    let conversationExitDate = createDateFromSpecificDateString("");

    if (pendingInteraction) {
      conversationStatusImageName = "orange-ellipse";
      conversationStatuskey = "PENDING_INTERACTION";
    }
    if (exitedByMatch && exitedByClient) {
      conversationStatusImageName = "red-ellipse";
      if (exitedByClientDate < exitedByMatchDate) {
        conversationStatuskey = "EXITED_BY_CLIENT";
        conversationExitDate = exitedByClientDate;
      } else {
        conversationStatuskey = "EXITED_BY_MATCH";
        conversationExitDate = exitedByMatchDate;
      }
    }

    if (exitedByMatch && !exitedByClient) {
      conversationStatusImageName = "red-ellipse";
      conversationStatuskey = "EXITED_BY_MATCH";
      conversationExitDate = exitedByMatchDate;
    }

    if (!exitedByMatch && exitedByClient) {
      conversationStatusImageName = "red-ellipse";
      conversationStatuskey = "EXITED_BY_CLIENT";
      conversationExitDate = exitedByClientDate;
    }

    return {
      conversationStatuskey,
      conversationExitDate,
      conversationStatusImageName
    };
  };

  getConversationStatusColor = () => {
    switch (this.getConversationHeaderStatusValues().conversationStatuskey) {
      case E_KHATTABAT_102_CONVERSATION_STATUS.ONGOING:
        return "success";
      case E_KHATTABAT_102_CONVERSATION_STATUS.PENDING_INTERACTION:
        return "warning";
      default:
        return "error";
    }
  };

  getShouldShowExitDate = () => {
    switch (this.getConversationHeaderStatusValues().conversationStatuskey) {
      case E_KHATTABAT_102_CONVERSATION_STATUS.ONGOING:
      case E_KHATTABAT_102_CONVERSATION_STATUS.PENDING_INTERACTION:
        return false;
      default:
        return true;
    }
  };
}

const getKhattabat102Store = memoize((conversationID: string) => {
  const store = new Khattabat102Store();
  store.conversationID.set(conversationID);
  return store;
});
export default getKhattabat102Store;
